import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import StudentInformation from '@/features/invitations/components/StudentInformation';
import useAcceptInvitation from '@/hooks/invitations/useAcceptInvitation';
import useToast from '@/hooks/store/useToast';
import { type InvitationStudent } from '@/interfaces/invitation';

import styles from './AcceptConfirmForm.module.scss';

type Props = {
  student: InvitationStudent;
  invitationToken: string;
  setComplete: React.Dispatch<React.SetStateAction<boolean>>;
};

const AcceptConfirmForm: React.FC<Props> = ({
  student,
  invitationToken,
  setComplete,
}) => {
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const navigate = useNavigate();
  const {
    mutate,
    isPending: isRegistering,
    error: acceptError,
  } = useAcceptInvitation();
  const { showToast } = useToast();

  const apiError =
    acceptError && acceptError.status >= 500
      ? ['予期しないエラーが発生しました。時間をおいて再度お試しください']
      : undefined;

  const onSubmit = () => {
    mutate(JSON.stringify({ invitation_token: invitationToken }), {
      onSuccess: () => {
        setComplete(true);
        navigate('/invitations/accept/complete');
      },
      onError: (err) => {
        if (err.status < 500) {
          if (err.status === 401) {
            showToast('エラーが発生しました。ログイン情報を確認してください');
            return;
          }

          navigate('/mypage/profile');
          showToast('生徒の登録ができませんでした');
        }
      },
    });
  };

  return (
    <div className={styles.AcceptConfirmForm}>
      <div className={styles.AcceptConfirmForm__Description}>
        以下の生徒について招待が届きました。
        <span className={styles.AcceptConfirmForm__Description__LineBreak}>
          {'\n'}
        </span>
        よろしければチェックの上、登録ボタンを押してください。
      </div>

      <StudentInformation
        organizationMembershipCode={student.code}
        organizationName={student.organization_name}
        name={student.name}
        grade={student.grade}
        classNameLabel={student.class_name}
        attendanceNumber={student.attendance_number}
        onCheckboxChange={() => {
          setButtonDisabled((prev) => !prev);
        }}
      />
      <div className={styles.AcceptConfirmForm__SubmitButtonArea}>
        <ul className={styles.AcceptConfirmForm__SubmitButtonArea__Note}>
          <li>
            <p>
              生徒情報（年・組・番・氏名）が誤っている場合は、招待した方にご確認ください。
            </p>
          </li>
        </ul>
        {apiError && (
          <ErrorList
            className={styles.AcceptConfirmForm__SubmitButtonArea__Error}
            errors={apiError}
          />
        )}
        <Button
          className={styles.AcceptConfirmForm__SubmitButtonArea__SubmitButton}
          color="primary"
          type="button"
          disabled={buttonDisabled || isRegistering}
          onClick={() => {
            onSubmit();
          }}
        >
          登録
        </Button>
        <Button
          className={styles.AcceptConfirmForm__SubmitButtonArea__CancelButton}
          color="outline"
          type="button"
          onClick={() => {
            window.location.href = '/mypage/profile';
          }}
        >
          キャンセル
        </Button>
      </div>
    </div>
  );
};

export default AcceptConfirmForm;
